import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isNonEmptyString } from "@/utils/string";
import { memo, useCallback } from "react";
import type { Attribute, FieldRenderInputProps } from "../../types";
import { getAttributeOptions } from "../../utils/option-utils";
import { extractValueFromValueLabel } from "../../utils/value-label-utils";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

function getAttributeDivisionDisplayLabel(
  attribute: Attribute,
  value: string | null,
): string {
  if (!attribute) {
    return "";
  }

  if (!value) {
    return attribute.label;
  }
  const options = getAttributeOptions(attribute);
  return options.find((i) => i.value === value)?.label || attribute.label;
}

/**
 * セレクトフィールドコンポーネント
 * プリセットされた選択肢から値を選択する機能を提供します
 */
export const SelectField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        error: fieldError,
        placeholder,
        disabled: isDisabled,
        readOnly: isReadOnly,
        "data-testid": inputTestId,
      }: FieldRenderInputProps) => {
        const selectValue = extractValueFromValueLabel(inputValue);

        if (!field) {
          return <div></div>;
        }

        const handleValueChange = (newValue: string) => {
          if (isNonEmptyString(newValue)) {
            handleChange(newValue);
          } else {
            handleChange(undefined);
          }
        };

        return (
          <Select
            value={selectValue}
            onValueChange={handleValueChange}
            disabled={isDisabled || isReadOnly}
            onOpenChange={(open) => {
              if (open && handleFocus) handleFocus();
              if (!open && handleBlur) handleBlur();
            }}
          >
            <SelectTrigger
              className={`w-full ${fieldError ? "border-red-500" : ""}`}
              data-testid={inputTestId}
              data-role="form-field-input"
              aria-invalid={!!fieldError}
              disabled={isReadOnly}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              {getAttributeOptions(field).map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {getAttributeDivisionDisplayLabel(field, item.value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
      [field],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={readOnly}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

SelectField.displayName = "SelectField";
